import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API } from '../config/api';
import axios from "axios";
import { errorMessage } from "../utils/errorMessage";
import { parseBlobError } from "../utils/parseBlobError";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import InvoiceRequestDetail from "./detail/InvoiceRequestDetail";
import PaymentRequestDetail from "./detail/PaymentRequestDetail";
import FileClosureDetail from "./detail/FileClosureDetail";
import RiskAssessmentDetail from "./detail/RiskAssessmentDetail";
import CompletionStatementDetail from "./detail/CompletionStatementDetail";
import PostCompletionDetail from "./detail/PostCompletionDetail";
import AccountsInstructionDetail from "./detail/AccountsInstructionDetail";
import MatterIntakeDetail from "./detail/MatterIntakeDetail";
import { DeleteSubmissionModal } from "../components/SubmissionLayout";
import { FileText, Loader2 } from "lucide-react";
import { format, parseISO } from "date-fns";
import { fmtUK } from "../lib/dateUtils";
import BonsaiFirmAssist from "../components/BonsaiFirmAssist";
import MatterActivityFeed from "../components/MatterActivityFeed";

export default function SubmissionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [submission, setSubmission] = useState(null);
  const [preview, setPreview] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [creatingSharePoint, setCreatingSharePoint] = useState(false);
  const [isDirector, setIsDirector] = useState(false);
  const [currentUserEmail, setCurrentUserEmail] = useState("");
  const [currentUserName, setCurrentUserName] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchSubmission();
    checkDirectorAccess();
  }, [id]);

  const checkDirectorAccess = async () => {
    try {
      const userId = sessionStorage.getItem("bonsai_user_id");
      if (!userId) return;
      const resp = await axios.get(`${API}/user-role`, {
        headers: { "X-Bonsai-User-Id": userId }
      });
      setIsDirector(resp.data?.is_director === true);
      setCurrentUserEmail(resp.data?.email || "");
      setCurrentUserName(resp.data?.name || "");
    } catch { /* non-critical */ }
  };

  const handleDeleteSubmission = async () => {
    if (!deleteReason.trim()) {
      toast.error("A deletion reason is required");
      return;
    }
    setDeleting(true);
    try {
      const userId = sessionStorage.getItem("bonsai_user_id");
      await axios.delete(`${API}/submissions/${id}`, {
        data: { reason: deleteReason.trim() },
        headers: { "X-Bonsai-User-Id": userId }
      });
      toast.success("Submission deleted successfully");
      setShowDeleteModal(false);
      navigate("/");
    } catch (err) {
      toast.error(errorMessage(err, "Failed to delete submission"));
    } finally {
      setDeleting(false);
    }
  };

  const fetchSubmission = async () => {
    try {
      const response = await axios.get(`${API}/submissions/${id}`);
      setSubmission(response.data);
      
      // Fetch form-specific data
      const formType = response.data?.form_type || response.data?.data?.form_type || "matter_intake";
      
      if (formType === "matter_intake" || formType === "pre_caseminder") {
        fetchPreview();
      } else if (formType === "invoice_request" || formType === "payment_request") {
        // Both invoice and payment requests may have files
        fetchFiles();
      }
    } catch (error) {
      console.error("Failed to fetch submission:", error);
      const detail = error?.response?.data?.detail;
      const status = error?.response?.status;
      if (detail) {
        toast.error(`Failed to load submission: ${detail}`);
      } else if (status) {
        toast.error(`Failed to load submission (HTTP ${status})`);
      } else {
        toast.error("Failed to load submission");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchPreview = async () => {
    try {
      const response = await axios.get(`${API}/submissions/${id}/preview`);
      setPreview(response.data);
    } catch (error) {
      console.error("Failed to fetch preview:", error);
    }
  };

  const fetchFiles = async () => {
    try {
      const response = await axios.get(`${API}/submissions/${id}/files`);
      setFiles(response.data);
    } catch (error) {
      console.error("Failed to fetch files:", error);
    }
  };

  const handleDownloadCCL = async () => {
    setDownloading(true);
    try {
      const response = await axios.post(
        `${API}/submissions/${id}/generate-letter`,
        {},
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const contentDisposition = response.headers['content-disposition'];
      let filename = `CCL_${preview?.client_name?.replace(/\s+/g, '_')}_${preview?.our_ref?.replace(/\s+/g, '_')}.docx`;
      if (contentDisposition) {
        const matches = contentDisposition.match(/filename=(.+)/);
        if (matches) {
          filename = matches[1].replace(/"/g, '');
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Client Care Letter downloaded successfully!");
    } catch (error) {
      console.error("Download failed:", error);
      const detail = await parseBlobError(error);
      toast.error(`Failed to download letter: ${detail}`);
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadPDF = async () => {
    setDownloadingPdf(true);
    try {
      const response = await axios.get(
        `${API}/submissions/${id}/download-pdf`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const contentDisposition = response.headers['content-disposition'];
      const formType = submission?.data?.form_type;
      const formTypeLabels = {
        "invoice_request": "Invoice Request",
        "payment_request": "Payment Request",
        "file_closure": "File Closure"
      };
      const formLabel = formTypeLabels[formType] || "PDF";
      let filename = `${formLabel.replace(/\s+/g, '_')}_${submission?.data?.matter_reference?.replace(/\s+/g, '_')}.pdf`;
      if (contentDisposition) {
        const matches = contentDisposition.match(/filename=(.+)/);
        if (matches) {
          filename = matches[1].replace(/"/g, '');
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${formLabel} PDF downloaded!`);
    } catch (error) {
      console.error("Download failed:", error);
      const detail = await parseBlobError(error);
      toast.error(`Failed to download PDF: ${detail}`);
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleDownloadFile = async (fileId, filename) => {
    try {
      const response = await axios.get(
        `${API}/submissions/${id}/files/${fileId}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("File downloaded!");
    } catch (error) {
      console.error("Download failed:", error);
      const detail = await parseBlobError(error);
      toast.error(`Failed to download file: ${detail}`);
    }
  };

  const handleExportJson = async () => {
    try {
      const response = await axios.get(
        `${API}/submissions/${id}/export-json`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const formType = submission?.data?.form_type || "matter_intake";
      let filename = formType === "invoice_request" 
        ? `Invoice_Request_${submission?.data?.matter_reference?.replace(/\s+/g, '_')}.json`
        : `Submission_${preview?.client_name?.replace(/\s+/g, '_')}.json`;
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Submission data exported successfully!");
    } catch (error) {
      console.error("Export failed:", error);
      const detail = await parseBlobError(error);
      toast.error(`Failed to export data: ${detail}`);
    }
  };

  const handleCreateSharePointMatter = async () => {
    setCreatingSharePoint(true);
    try {
      const response = await axios.post(`${API}/submissions/${id}/create-sharepoint-matter`);
      
      if (response.data.already_created) {
        toast.info("SharePoint matter was already created for this submission");
      } else if (response.data.folder_created || response.data.sharepoint_enabled === false) {
        toast.success("SharePoint matter created successfully!");
        // Refresh submission to get updated SharePoint status
        fetchSubmission();
      } else {
        toast.warning("SharePoint creation completed with some issues");
      }
    } catch (error) {
      console.error("SharePoint creation failed:", error);
      const errorMsg = errorMessage(error, "Failed to create SharePoint matter");
      toast.error(errorMsg);
    } finally {
      setCreatingSharePoint(false);
    }
  };

  const formatDate = (dateStr) => fmtUK(dateStr, "dd MMMM yyyy, HH:mm");

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-2 border-teal border-t-transparent rounded-md animate-spin" />
          <p className="mt-4 text-slate-500">Loading submission...</p>
        </div>
      </div>
    );
  }

  if (!submission) {
    return (
      <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center">
        <div className="text-center">
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-medium text-teal mb-2">Submission Not Found</h2>
          <p className="text-slate-500 mb-4">The requested submission could not be found.</p>
          <Button onClick={() => navigate("/")} variant="outline">
            Return to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const formType = submission?.form_type || submission?.data?.form_type || "matter_intake";

  // Shared delete props for sub-components
  const deleteProps = {
    isDirector,
    onDelete: () => setShowDeleteModal(true),
  };

  // ── Brief B2 (mount-orphans-r1) — Bonsai Firm Assist preamble ──────
  // Single mount point above every type-specific detail component so
  // the AI panel appears for all 8 submission types without changing
  // any sub-component. Component owns its own internal idle/loading/
  // error states — built-in error renders here on failure (no silent
  // fallback per V's standing rule).
  const firmAssistPanel = (
    <BonsaiFirmAssist
      submissionId={id}
      matterType={submission?.data?.Q9 || submission?.form_type || ""}
      data-testid="submission-detail-firm-assist"
    />
  );

  // Render File Closure view
  if (formType === "file_closure") {
    return <>
      {firmAssistPanel}
      <FileClosureDetail 
        submission={submission} 
        formatDate={formatDate}
        handleDownloadPDF={handleDownloadPDF}
        handleExportJson={handleExportJson}
        downloadingPdf={downloadingPdf}
        navigate={navigate}
        {...deleteProps}
      />
      <DeleteSubmissionModal show={showDeleteModal} onClose={() => setShowDeleteModal(false)} reason={deleteReason} setReason={setDeleteReason} onConfirm={handleDeleteSubmission} deleting={deleting} />
    </>;
  }

  // Render Payment Request view
  if (formType === "payment_request") {
    return <>
      {firmAssistPanel}
      <PaymentRequestDetail 
        submission={submission} 
        formatDate={formatDate}
        handleDownloadPDF={handleDownloadPDF}
        handleExportJson={handleExportJson}
        downloadingPdf={downloadingPdf}
        navigate={navigate}
        {...deleteProps}
      />
      <DeleteSubmissionModal show={showDeleteModal} onClose={() => setShowDeleteModal(false)} reason={deleteReason} setReason={setDeleteReason} onConfirm={handleDeleteSubmission} deleting={deleting} />
    </>;
  }

  // Render Invoice Request view
  if (formType === "invoice_request") {
    return <>
      {firmAssistPanel}
      <InvoiceRequestDetail 
        submission={submission} 
        files={files}
        formatDate={formatDate}
        handleDownloadPDF={handleDownloadPDF}
        handleDownloadFile={handleDownloadFile}
        handleExportJson={handleExportJson}
        downloadingPdf={downloadingPdf}
        navigate={navigate}
        {...deleteProps}
      />
      <DeleteSubmissionModal show={showDeleteModal} onClose={() => setShowDeleteModal(false)} reason={deleteReason} setReason={setDeleteReason} onConfirm={handleDeleteSubmission} deleting={deleting} />
    </>;
  }

  // Render Risk Assessment view
  if (formType === "risk_assessment") {
    return <>
      {firmAssistPanel}
      <RiskAssessmentDetail 
        submission={submission}
        formatDate={formatDate}
        handleExportJson={handleExportJson}
        navigate={navigate}
        {...deleteProps}
      />
      <DeleteSubmissionModal show={showDeleteModal} onClose={() => setShowDeleteModal(false)} reason={deleteReason} setReason={setDeleteReason} onConfirm={handleDeleteSubmission} deleting={deleting} />
    </>;
  }

  // Render Completion Statement view
  if (formType === "completionstatement") {
    return <>
      {firmAssistPanel}
      <CompletionStatementDetail 
        submission={submission}
        formatDate={formatDate}
        navigate={navigate}
        currentUserEmail={currentUserEmail}
        currentUserName={currentUserName}
        {...deleteProps}
      />
      <DeleteSubmissionModal show={showDeleteModal} onClose={() => setShowDeleteModal(false)} reason={deleteReason} setReason={setDeleteReason} onConfirm={handleDeleteSubmission} deleting={deleting} />
    </>;
  }

  // Render Post-Completion Checklist view
  if (formType === "postcompletion") {
    return <>
      {firmAssistPanel}
      <PostCompletionDetail 
        submission={submission}
        formatDate={formatDate}
        navigate={navigate}
        {...deleteProps}
      />
      <DeleteSubmissionModal show={showDeleteModal} onClose={() => setShowDeleteModal(false)} reason={deleteReason} setReason={setDeleteReason} onConfirm={handleDeleteSubmission} deleting={deleting} />
    </>;
  }

  // Render Completion Funds Request view
  if (formType === "accountsinstruction") {
    return <>
      {firmAssistPanel}
      <AccountsInstructionDetail 
        submission={submission}
        formatDate={formatDate}
        navigate={navigate}
        {...deleteProps}
      />
      <DeleteSubmissionModal show={showDeleteModal} onClose={() => setShowDeleteModal(false)} reason={deleteReason} setReason={setDeleteReason} onConfirm={handleDeleteSubmission} deleting={deleting} />
    </>;
  }

  // Render Matter Intake view (default) - also handles pre_caseminder
  return <>
    {firmAssistPanel}
    <MatterIntakeDetail 
      submission={submission}
      preview={preview}
      formatDate={formatDate}
      handleDownloadCCL={handleDownloadCCL}
      handleExportJson={handleExportJson}
      handleCreateSharePointMatter={handleCreateSharePointMatter}
      downloading={downloading}
      creatingSharePoint={creatingSharePoint}
      navigate={navigate}
      isPreCaseMinder={formType === "pre_caseminder"}
      {...deleteProps}
    />
    {submission?.data?.matter_reference && (
      <MatterActivityFeed matterReference={submission.data.matter_reference} />
    )}
    <DeleteSubmissionModal show={showDeleteModal} onClose={() => setShowDeleteModal(false)} reason={deleteReason} setReason={setDeleteReason} onConfirm={handleDeleteSubmission} deleting={deleting} />
  </>;
}

